/* eslint-disable */
import uuid from 'uuid/v4';
import reject from 'lodash/reject';
import forEach from 'lodash/forEach';
import isFunction from 'lodash/isFunction';
import noop from 'lodash/noop';
import extend from 'lodash/extend';
import isEmpty from 'lodash/isEmpty';
import axios from 'axios';
import { getTrackingPathName } from '@AppRoutes/AppRoutesSelectors';
import { websocketsAreUnstableToastMessage } from '@Containers/ToastMessages/ToastMessagesActions/ToastMessagesActions';
import { RxSocketUrls } from '@Constants/CommonConstants';
import { config } from '@Constants/rx-ui/constants';
import RequestUrls from '@Constants/RequestUrlsConstants';
import HttpRequest from '@Services/RequestFactory';
import NewRelicService from '@Services/NewRelicService';
import * as AuthHelper from '@Services/Auth/AuthHelper';
import { generateTraceHeaders } from '@Services/CorrelationId';
import { generateRsHeader } from '@Services/RsHeader';
import Deferred from '@Utils/Deferred';

import Stomp from 'stompjs';
import SockJS from 'sockjs-client';
import store from '@Store';

const RxSocketInterceptions = {
  request: [], // Interceptors for requests. arg[0]: Request obj
  response: [], // Interceptors for responses. arg[0]: Response obj
};

function addSourceKey(request) {
  const stateCurrentName = getTrackingPathName(store.getState()) || '';
  // RXFE-3377
  // NOTE: sourceKey header is used in statistica usage reporting
  if (!request.ignoreAllSpecificHeaders && !request?.headers?.sourceKey) {
    request.headers = {
      sourceKey: stateCurrentName,
      ...request.headers,
    };
  }
  return request;
}

let connectUrl;
let sockConnected = false;
let bufferCallbacks = [];
let bufferSends = [];
let currentSubscriptions = [];
let connectCallbacks = [];
let closeCallbacks = [];
let delayedConnect = Deferred();
let stompClient;
let socket;
let unsubscribeClose;

const closeSubscribeGenerator = subscribeGenerator(closeCallbacks);
const connectSubscribeGenerator = subscribeGenerator(connectCallbacks);

/**
 * @method subscriptionExist
 * @param channel name
 * return subscription object or false if subscription does not exist
 */
function subscriptionExist(channel) {
  let subscription = false;
  forEach(currentSubscriptions, (sub) => {
    if (channel.localeCompare(sub.channel) === 0) {
      subscription = sub.subscription;
    }
  });

  return subscription;
}

/**
 * @method unsubscribeIfExist
 * @param channel name
 * unsubscribes from channel if previous subscription exists
 */
function unsubscribeIfExist(channel) {
  const subscription = subscriptionExist(channel);
  if (subscription) {
    currentSubscriptions = reject(currentSubscriptions, (sub) => {
      return subscription === sub.subscription;
    });
    subscription.unsubscribe();
  }
}

/**
 * @method connect
 * Sets connection to server. After connection sets handler for subscribes and sends data on server from buffer
 */
function connect(useCdn = false) {
  const httpTimeout = 2000;
  const isNoWebsockets = false;
  const accessToken = AuthHelper.getAccessToken();

  if (sockConnected) {
    // RX-11447
    if (connectUrl && connectUrl.indexOf(accessToken) === -1) {
      // In case connection should be established with a new access token,
      // but previous connection is still active for some reason - disconnect first
      disconnect();
    } else {
      // In case connection should be established with the same access token,
      // but previous connection is still active for some reason - just ignore it
      return;
    }
  }

  /* jslint camelcase: false */
  const prefix = useCdn ? 'restPrefix' : 'wsPrefix';
  connectUrl = HttpRequest.getUrlWithPrefix(RxSocketUrls.ConnectionUrl + accessToken, prefix);
  /* jslint camelcase: true */

  onConnect();

  unsubscribeClose = closeSubscribeGenerator(function (status) {
    sockConnected = false;
    // check if server is ready for reconnect websockets
    let checkServerStatus = function (url, timeout) {
      let checkingHandler;
      const accessToken = AuthHelper.getAccessToken();

      if (accessToken) {
        checkingHandler = setTimeout(() => {
          axios
            .get(url)
            .then(function () {
              connect(useCdn);
            })
            .catch(function (error) {
              if (error.status === 401 && !useCdn) {
                // if token problem and current attempt is not through CDN already
                // RXFE-6345 -
                // If BE mismatch between WS server and CDN (when WS server switched to a different instance):
                // accessToken still valid for CDN requests but no longer valid for WS HTTP request.
                // In This case will try to connect to socket via CDN.

                const customAttributes = {
                  connectUrl: url.split('?')[0],
                  messageDetail: 'Websocket connection is unstable',
                };
                axios
                  .get(RequestUrls.checkToken.url, { params: { token: accessToken } })
                  .then(() => {
                    connect(true);
                  })
                  .catch((error) => {
                    NewRelicService.noticeError(error, customAttributes);
                    Logger.error(error);
                  });
              } else {

                const message = 'WebSocket connect attempt error';
                const updatedError = typeof error === 'string' ? message : { ...error, message };

                // Define customAttributes for the else block
                const customAttributes = {
                  connectUrl: url.split('?')[0],
                  messageDetail: 'Websocket connection is unstable',
                };

                const isCodeIssue = error.status || error.statusCode;

                const errorDetails = {
                  status: error.status || error.statusCode,
                  message: error.message || message,
                  url: url,
                  errorType: isCodeIssue ? 'server_error' : 'network_error',
                  fullError: JSON.stringify(error, Object.getOwnPropertyNames(error))
                };

                Logger.error('WebSocket connect attempt error', errorDetails);
                websocketsAreUnstableToastMessage();

                NewRelicService.noticeError(updatedError, {...customAttributes, ...errorDetails});
                checkServerStatus(url, timeout * 2);
              }
            })
            .finally(function () {
              clearTimeout(checkingHandler);
            });
        }, timeout);
      }
    };
    checkServerStatus(connectUrl, httpTimeout);
    if (unsubscribeClose) {
      unsubscribeClose();
      unsubscribeClose = null;
    }
  });

  RxSocketInterceptions.request.push(addSourceKey);

  const sockjsOpts = {
    transportMinTimeout: 2500, // 5000 ms round trip
    sessionId: () => uuid(), // random WS session id
  };

  if (isNoWebsockets) {
    sockjsOpts.transports = [
      /*'websocket',*/
      'xhr-streaming',
      'xdr-streaming' /*,
        'eventsource',
        'iframe-eventsource',
        'htmlfile',
        'iframe-htmlfile',
        'xhr-polling',
        'xdr-polling',
        'iframe-xhr-polling',
        'jsonp-polling'*/,
    ];
  } else if (AuthHelper.isFeatureEnabled('useHttpPolling')) {
    sockjsOpts.transports = [
      /*'websocket',
      'xhr-streaming',
      'xdr-streaming',
      'eventsource',
      'iframe-eventsource',
      'htmlfile',
      'iframe-htmlfile',*/
      'xhr-polling',
      'xdr-polling',
      /*'iframe-xhr-polling',
      'jsonp-polling'*/
    ];
  } else {
    sockjsOpts.transports = [
      'websocket',
      'xhr-streaming',
      'xdr-streaming' /*,
        'eventsource',
        'iframe-eventsource',
        'htmlfile',
        'iframe-htmlfile',
        'xhr-polling',
        'xdr-polling',
        'iframe-xhr-polling',
        'jsonp-polling'*/,
    ];
  }

  socket = new SockJS(connectUrl, sockjsOpts);
  stompClient = Stomp.over(socket);

  // disables excessive logging from the stompClient
  stompClient.debug = null;

  stompClient.connect(
    {},
    () => {
      sockConnected = true;

      forEach(bufferCallbacks, (value) => {
        value.unregisterFunc = subscribe(value.destination, value.callback);
      });

      forEach(bufferSends, (value) => {
        stompClient.send(value.destination, value.headers, JSON.stringify(value.payload));
      });

      bufferCallbacks = [];
      bufferSends = [];

      delayedConnect.resolve();
    },
    (err) => {
      onError(err);
      onClose(err);
    }
  );
}

/**
 * @method subscribe
 * Sets handler for subscription. If socket not connected adds handler in buffer
 */
function subscribeOut(destination, callback) {
  if (destination && isFunction(callback)) {
    if (sockConnected) {
      return subscribe(destination, callback);
    }
    //Logger.info('No WebSockets connected, the request "' + destination + '" was added to the queue.');
    const obj = {
      destination: destination,
      callback: callback,
      unregister: function () {
        if (obj.unregisterFunc) {
          obj.unregisterFunc();
        } else {
          bufferCallbacks.splice(bufferCallbacks.indexOf(obj), 1);
        }
      },
      unregisterFunc: noop,
    };
    bufferCallbacks.push(obj);
    return obj.unregister;
  }
}

/**
 * @method send
 * Sends data on server. If socket not connected add data in buffer
 */
function send(destination, payload, headers, forbidBuffering) {
  let defaultHeaders = {};
  const devOptions = AuthHelper.getDevOptions();
  if (!isEmpty(devOptions)) {
    defaultHeaders[config.CONTROL_HEADER] = AuthHelper.getDevOptions();
  }
  extend(defaultHeaders, generateTraceHeaders(), generateRsHeader(destination));
  const interceptorParams = {
    destination: destination,
    payload: payload,
    headers: extend(defaultHeaders, headers || {}),
  };

  if (destination !== RxSocketUrls.ChannelsUrls.Heartbeat) {
    //heartbeat should not prolong the session
    AuthHelper.setLastActionTime();
  }

  RxSocketInterceptions.request.forEach((interceptor) => {
    interceptor(interceptorParams);
  });
  try {
    if (sockConnected) {
      stompClient.send(interceptorParams.destination, interceptorParams.headers, JSON.stringify(interceptorParams.payload));
    } else if (!forbidBuffering) {
      bufferSends.push(interceptorParams);
    } else {
      Logger.error(`Websockets are not connected, the request "${destination}" was not sent`);
    }
  } catch (e) {
    Logger.error('Error in sending data to server', e);
  }
}

/**
 * @method disconnect
 * Disconnects current webSocket connection
 */
function disconnect(callback) {
  Logger.info('Disconnect called: ', sockConnected);
  if (sockConnected) {
    stompClient.disconnect(callback);
    socket.close();
    sockConnected = false;
    currentSubscriptions = [];
    delayedConnect = Deferred();
  }

  // Clear from previous connection, new connect will create a new one
  if (unsubscribeClose) {
    unsubscribeClose();
    unsubscribeClose = null;
  }
}

function onError(data) {
  Logger.error('Socket error', data);
}

function subscribe(destination, callback) {
  const subscription = stompClient.subscribe(destination, (data) => {
    const response = {
      destination: destination,
      body: JSON.parse(data.body),
      headers: data.headers || {},
      response: data,
    };

    callback.call(this, response.body, response.headers, response.response);
  });
  currentSubscriptions.push({ channel: destination, subscription: subscription });
  return subscription.unsubscribe;
}

function subscribeGenerator(storage) {
  return function (callback) {
    if (!callback || !isFunction(callback)) {
      return null;
    }
    callback.unregister = function () {
      const index = storage.indexOf(callback);
      if (index !== -1) storage.splice(index, 1);
    };

    storage.push(callback);

    return callback.unregister;
  };
}

function onClose(event) {
  Logger.warn('Websockets offline');

  RxSocketInterceptions.request = [];
  RxSocketInterceptions.response = [];

  forEach(closeCallbacks, (value) => {
    setTimeout(() => {
      value(event);
    }, 0);
  });
}

function onConnect(event) {
  Logger.info('Websockets online');
  forEach(connectCallbacks, (value) => {
    setTimeout(() => {
      value(event);
    }, 0);
  });
}

function onMessage(event) {
  RxSocketInterceptions.response.forEach((interceptor) => {
    interceptor(event.data);
  });
}

const getDelayedConnectPromise = () => delayedConnect.promise;
const getConnectUrl = () => connectUrl;

export {
  connect,
  subscribeOut as subscribe,
  send,
  disconnect,
  closeSubscribeGenerator as onClose,
  connectSubscribeGenerator as onConnect,
  subscriptionExist,
  getDelayedConnectPromise,
  unsubscribeIfExist,
  getConnectUrl,
};
